export const buildGroupEventDetailRows = ({
  isSelectedGroupUnified = false,
  effectiveGroupEvents = [],
  groupEventSidebarRows = [],
  pickFirstString,
  toDateInput,
  formatDateJP,
  toTimeInput,
  getExtraRowFields,
}) => {
  if (!isSelectedGroupUnified) return [];

  const detailsByEventUuid = new Map(
    (Array.isArray(groupEventSidebarRows) ? groupEventSidebarRows : [])
      .filter((row) => row?.event_uuid)
      .map((row) => [String(row.event_uuid), row])
  );

  return (Array.isArray(effectiveGroupEvents) ? effectiveGroupEvents : []).map((event, index) => {
    const eventUuid = String(event?.event_uuid || '');
    const detail = detailsByEventUuid.get(eventUuid) || null;
    const merged = detail ? { ...event, ...detail } : event;
    const performance = pickFirstString?.(merged?.performance_type, event?.performance_type)
      || `公演${index + 1}（未設定）`;
    const eventDateInput = toDateInput?.(merged?.event_date) || '';
    const date = formatDateJP?.(eventDateInput) || '未設定';
    const open = toTimeInput?.(merged?.open_time) || '未設定';
    const start = toTimeInput?.(merged?.start_time) || '未設定';
    const sortTime = toTimeInput?.(merged?.start_time || merged?.open_time) || '00:00';
    const sortKey = `${eventDateInput || '9999-12-31'} ${sortTime}`;
    const venueId = merged?.venue_id ?? merged?.venueId ?? merged?.venue?.id ?? null;
    const venueName = pickFirstString?.(merged?.venue_name, merged?.venue, merged?.place) || '会場未設定';
    const pref = pickFirstString?.(merged?.prefecture_name, merged?.prefecture);
    const city = pickFirstString?.(merged?.city, merged?.city_name);
    const addrLine = pickFirstString?.(merged?.address, merged?.venue_address);
    const address = [pref, city, addrLine].filter(Boolean).join(' ') || '所在地未設定';
    const note = pickFirstString?.(merged?.note, merged?.remarks, merged?.memo);
    const mapQuery = venueName !== '会場未設定' ? venueName : address;
    const mapLinkUrl = mapQuery && mapQuery !== '所在地未設定'
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`
      : '';

    const baseRow = {
      key: eventUuid || `group-event-${index}`,
      eventUuid,
      performance,
      eventDateInput,
      sortKey,
      date,
      open,
      start,
      venueId,
      venueName,
      address,
      note,
      mapLinkUrl,
    };

    const extra = typeof getExtraRowFields === 'function'
      ? getExtraRowFields({
          event,
          detail,
          merged,
          eventUuid,
          index,
        })
      : null;
    return extra && typeof extra === 'object'
      ? { ...baseRow, ...extra }
      : baseRow;
  });
};

export const buildEventScheduleFromInfo = ({
  selectedEventInfo,
  toDateInput,
  formatDateJP,
  toTimeInput,
  includeLabel = false,
}) => {
  if (!selectedEventInfo) {
    return includeLabel
      ? { date: '', time: '', label: '' }
      : { date: '', time: '' };
  }
  const dateInput = toDateInput?.(selectedEventInfo.event_date);
  const dateLabel = formatDateJP?.(dateInput) || '';
  const timeLabel = toTimeInput?.(selectedEventInfo.start_time || selectedEventInfo.open_time) || '';
  if (!includeLabel) return { date: dateLabel, time: timeLabel };
  const label = dateLabel && timeLabel
    ? `${dateLabel} ${timeLabel}`
    : (dateLabel || timeLabel || '');
  return { date: dateLabel, time: timeLabel, label };
};

export const buildUnifiedEventHeaderSchedule = ({
  isSelectedGroupUnified = false,
  eventSchedule = {},
  groupEventSidebarRows = [],
  effectiveGroupEvents = [],
  toDateInput,
  formatDateJP,
}) => {
  if (!isSelectedGroupUnified) {
    return { date: eventSchedule?.date || '', time: eventSchedule?.time || '' };
  }

  const sourceRows = (Array.isArray(groupEventSidebarRows) && groupEventSidebarRows.length > 0)
    ? groupEventSidebarRows
    : (Array.isArray(effectiveGroupEvents) ? effectiveGroupEvents : []);
  const dateInputs = sourceRows
    .map((row) => toDateInput?.(row?.event_date))
    .filter(Boolean);
  if (!dateInputs.length) {
    return { date: eventSchedule?.date || '', time: '' };
  }
  const uniqueSorted = Array.from(new Set(dateInputs)).sort();
  if (uniqueSorted.length === 1) {
    return { date: formatDateJP?.(uniqueSorted[0]) || eventSchedule?.date || '', time: '' };
  }
  const first = formatDateJP?.(uniqueSorted[0]) || uniqueSorted[0];
  const last = formatDateJP?.(uniqueSorted[uniqueSorted.length - 1]) || uniqueSorted[uniqueSorted.length - 1];
  return { date: `${first} ～ ${last}`, time: '' };
};

export const buildHeaderNextPerformance = ({
  isSelectedGroupUnified = false,
  groupEventDetailRows = [],
}) => {
  if (!isSelectedGroupUnified) return null;
  const rows = Array.isArray(groupEventDetailRows) ? groupEventDetailRows : [];
  if (!rows.length) return null;

  const sorted = [...rows].sort((a, b) => {
    const keyA = String(a?.sortKey || '9999-12-31 23:59');
    const keyB = String(b?.sortKey || '9999-12-31 23:59');
    if (keyA !== keyB) return keyA.localeCompare(keyB, 'en');
    return String(a?.eventUuid || '').localeCompare(String(b?.eventUuid || ''), 'en');
  });

  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date()).map((p) => [p.type, p.value])
  );
  const todayDateInput = `${parts.year}-${parts.month}-${parts.day}`;
  const nowKey = `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;

  const datedRows = sorted.filter((row) => Boolean(row?.eventDateInput));
  const latestEventDateInput = datedRows.length
    ? datedRows[datedRows.length - 1].eventDateInput
    : '';
  if (latestEventDateInput && latestEventDateInput < todayDateInput) {
    return null;
  }

  let target = datedRows.find((row) => String(row?.sortKey || '') >= nowKey) || null;
  if (!target) {
    target = datedRows.length ? datedRows[datedRows.length - 1] : sorted[0] || null;
  }
  if (!target) return null;

  const performance = String(target.performance || '').trim() || '公演未設定';
  const slashDate = target.eventDateInput ? String(target.eventDateInput).replace(/-/g, '/') : '';
  const label = slashDate ? `Next ${performance}（${slashDate}）` : `Next ${performance}`;
  return {
    eventUuid: target.eventUuid || '',
    label,
  };
};

export const buildHeaderNextMultiPerformance = ({
  isMultiPerformanceEvent = false,
  performances = [],
}) => {
  if (!isMultiPerformanceEvent) return null;
  const rows = (Array.isArray(performances) ? performances : [])
    .map((performance, index) => {
      const eventDateInput = String(
        performance?.performance_date ||
        performance?.performanceDate ||
        ''
      ).slice(0, 10);
      if (!eventDateInput) return null;
      const time = String(
        performance?.start_time ||
        performance?.performance_start_time ||
        performance?.open_time ||
        '00:00'
      ).slice(0, 5);
      return {
        performance:
          String(performance?.performance_name || performance?.performanceName || '').trim() ||
          `公演${index + 1}`,
        eventDateInput,
        sortKey: `${eventDateInput} ${time || '00:00'}`,
      };
    })
    .filter(Boolean);
  if (!rows.length) return null;

  return buildHeaderNextPerformance({
    isSelectedGroupUnified: true,
    groupEventDetailRows: rows,
  });
};

export const resolveHeaderFlyerSource = ({
  isSelectedGroupUnified = false,
  headerNextPerformanceEventUuid = '',
  groupEventSidebarRows = [],
  effectiveGroupEvents = [],
}) => {
  if (!isSelectedGroupUnified) return null;
  const nextEventUuid = String(headerNextPerformanceEventUuid || '').trim();
  if (!nextEventUuid) return null;
  const detailRow = (Array.isArray(groupEventSidebarRows) ? groupEventSidebarRows : [])
    .find((row) => String(row?.event_uuid || '').trim() === nextEventUuid);
  if (detailRow) return detailRow;
  const fallbackRow = (Array.isArray(effectiveGroupEvents) ? effectiveGroupEvents : [])
    .find((row) => String(row?.event_uuid || '').trim() === nextEventUuid);
  return fallbackRow || null;
};
